<template>
  <SparkModal :model-value="show" dismissable @update:model-value="close">
    <template #header>
      <div class="flex items-center">
        <button
          v-if="currentView === 'list'"
          class="mr-12 text-gray-600 hover:text-primary-600 transition-colors"
          @click="currentView = 'upload'"
        >
          <svg class="w-20 h-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span class="text-16">
          {{ currentView === 'upload' ? 'Upload Parts' : batchName || 'Uploading...' }}
        </span>
      </div>
    </template>
    <template #default>
      <div class="flex flex-col items-center justify-center p-24">
        <div class="w-full max-w-[600px]">
          <div v-if="currentView === 'upload'">
            <div class="flex flex-col gap-16 mb-24">
              <SparkInput v-model="batchName" label="Group Name" type="text" name="batch-name" />
              <div class="flex flex-col gap-8">
                <SparkCheckbox
                  v-model="checkBoxSkipDrawingAnalysis"
                  name="skip-drawing-analysis"
                  @change="newSkipDrawingAnalysisValue($event)"
                >
                  <div v-text="'Skip Drawing Analysis'" />
                </SparkCheckbox>
                <SparkCheckbox
                  v-model="checkBoxRipAssemblies"
                  name="rip-assemblies"
                  @change="newRipAssembliesValue($event)"
                >
                  <div v-text="'Rip Assemblies'" />
                </SparkCheckbox>
                <SparkCheckbox
                  v-model="checkBoxMarketPrices"
                  name="market-prices"
                  @change="newMarketPricesValue($event)"
                >
                  <div v-text="'Save market prices'" />
                </SparkCheckbox>
              </div>
            </div>

            <div class="flex flex-col items-end gap-8 mb-24">
              <div
                class="text-right text-13 text-primary-700 hover:text-primary-500 cursor-pointer"
                @click="downloadMetadataTemplate"
              >
                Download Template
              </div>
              <div class="text-right text-11 text-gray-500">
                Download and fill in the template to include meta data with your group upload.
                <a
                  href="https://support.3dspark.de/uploading-multiple-parts-batch-upload-part-groups"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-11 text-primary-500 cursor-pointer hover:text-primary-700"
                  >Need help?</a
                >
              </div>
            </div>

            <div
              class="relative flex flex-col items-center justify-center w-full h-[200px] border-2 border-dashed border-primary-300 rounded-8 bg-primary-50 transition-all duration-300 hover:border-primary-500 hover:bg-primary-100"
            >
              <div class="flex flex-col items-center justify-center gap-16">
                <div class="flex items-center justify-center w-48 h-48 rounded-full bg-primary-200">
                  <svg class="w-24 h-24 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div class="text-center">
                  <p class="text-16 font-medium text-gray-800">Drag and drop your files here</p>
                  <p class="text-14 text-gray-600 mt-4">or</p>
                  <button class="mt-4 text-14 font-medium text-primary-600 hover:text-primary-700">Browse Files</button>
                </div>
              </div>
              <input
                type="file"
                multiple
                accept=".stl,.stp,.step,.jpg,.jpeg,.png,.tif,.tiff,.pdf,.xlsx,.xlsm,.xlsb,.xltx,.xltm,.xls,.ppt,.pptx,.doc,.docx,.mp4,.mpg,.mpeg"
                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                @input="uploadFile"
                @click="resetFileUpload"
                @drop="resetFileUpload"
              />
            </div>
          </div>

          <!-- List View -->
          <div v-else>
            <div class="mb-24">
              <div class="flex items-center justify-between mb-12">
                <h3 class="text-16 font-medium text-gray-800">Upload Progress</h3>
                <div class="text-14 text-gray-600">
                  {{ getCompletedCount }} of {{ uploadQueue.length }} files uploaded
                </div>
              </div>
              <div class="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  class="h-full bg-primary-500 transition-all duration-300"
                  :style="{ width: `${(getCompletedCount / uploadQueue.length) * 100}%` }"
                ></div>
              </div>
            </div>

            <div class="space-y-8">
              <div
                v-for="(file, index) in uploadQueue"
                :key="index"
                class="flex items-center justify-between p-12 bg-white rounded-8 border border-gray-200"
              >
                <div class="flex items-center gap-12">
                  <div class="w-32 h-32 flex items-center justify-center bg-primary-50 rounded-4">
                    <svg class="w-16 h-16 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p class="text-14 font-medium text-gray-800">{{ file.data.cad || 'Metadata File' }}</p>
                    <p
                      class="text-12"
                      :class="{
                        'text-primary-600': file.status === 'completed',
                        'text-orange-500': file.status === 'uploading',
                        'text-red-500': file.status === 'error',
                        'text-gray-600': file.status === 'pending',
                      }"
                    >
                      {{ file.status }}
                    </p>
                  </div>
                </div>
                <div class="flex items-center">
                  <SparkSpinner v-if="file.status === 'uploading'" />
                  <button
                    v-if="file.status === 'pending'"
                    class="text-gray-400 hover:text-red-500 transition-colors duration-200"
                    @click="removeFile(index)"
                  >
                    <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="batchUploading && !uploadQueue.length" class="mt-24 flex justify-center">
              <SparkSpinner />
            </div>

            <div v-if="!batchUploading && uploadQueue.length" class="mt-24 flex justify-end">
              <SparkButton variant="secondary" @click="closeModalAndOpenBatch">Done</SparkButton>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SparkModal>
</template>

<script>
import to from 'await-to-js';
import readXlsxFile from 'read-excel-file';
import { mapState, mapActions } from 'vuex';

import SparkButton from '@/components/SparkComponents/SparkButton.vue';
import SparkCheckbox from '@/components/SparkComponents/SparkCheckbox.vue';
import SparkInput from '@/components/SparkComponents/SparkInput.vue';
import SparkModal from '@/components/SparkComponents/SparkModal.vue';
import SparkSpinner from '@/components/SparkComponents/SparkSpinner.vue';
import { getExtension, isExcel, isCAD } from '@/helpers';

export default {
  name: 'BatchUploadModal',

  components: {
    SparkModal,
    SparkButton,
    SparkCheckbox,
    SparkInput,
    SparkSpinner,
  },

  props: {
    show: { type: Boolean, default: false },
  },

  emits: ['close', 'batch-upload-complete'],

  data() {
    return {
      currentView: 'upload',
      batchUploading: false,
      batchName: '',
      checkBoxSkipDrawingAnalysis: false,
      checkBoxRipAssemblies: false,
      checkBoxMarketPrices: false,
      uploadQueue: [],
      currentUploadIndex: -1,
      batchPk: null,
    };
  },

  computed: {
    ...mapState(['part', 'partUploadStatus']),
    ...mapState('application', ['axiosInstance']),

    getCompletedCount() {
      return this.uploadQueue.filter(file => file.status === 'completed').length;
    },
  },

  watch: {
    partUploadStatus: {
      handler(newStatus) {
        if (this.partUploadStatus) {
          this.handlePartUploadFinished(newStatus);
        }
      },
    },
  },

  methods: {
    ...mapActions(['fetchBatches']),

    close() {
      this.$emit('close');
      this.currentView = 'upload';
      this.uploadQueue = [];
      this.batchUploading = false;
      this.batchName = '';
      this.checkBoxSkipDrawingAnalysis = false;
      this.checkBoxRipAssemblies = false;
      this.checkBoxMarketPrices = false;
    },

    closeModalAndOpenBatch() {
      this.$emit('batch-upload-complete', this.batchPk);
      this.close();
    },

    newSkipDrawingAnalysisValue(event) {
      this.checkBoxSkipDrawingAnalysis = event;
    },

    newRipAssembliesValue(event) {
      this.checkBoxRipAssemblies = event;
    },

    newMarketPricesValue(event) {
      this.checkBoxMarketPrices = event;
    },

    resetFileUpload(event) {
      event.target.value = null;
    },

    removeFile(index) {
      this.uploadQueue.splice(index, 1);
    },

    async uploadFile(event) {
      let files = event.target.files;
      let file_dict = {};
      let excel_file = null;

      this.uploadQueue = [];
      this.currentUploadIndex = -1;
      this.batchPk = null;

      for (let i = 0; i < files.length; ++i) {
        let file = files[i];
        let filename = file.name;
        if (isExcel(getExtension(filename))) {
          excel_file = file;
        }
        file_dict[filename] = file;
      }

      try {
        this.batchUploading = true;

        if (excel_file) {
          await this.processExcelFile(excel_file, file_dict);
        } else {
          await this.processCadFiles(file_dict);
        }

        this.currentView = 'list';

        let formData = new FormData();
        formData.append('name', this.batchName);
        formData.append('size', this.uploadQueue.length);

        let [err, response] = await to(this.axiosInstance.post('/api/v1/batch/', formData));
        if (err) {
          throw err;
        }

        this.batchPk = response.data.batch_pk;
        await this.$nextTick(() => {
          this.fetchBatches();
        });

        this.startNextUpload();
      } catch (error) {
        this.handleError(error, 'Failed to process files.');
        this.batchUploading = false;
      }
    },

    async processExcelFile(excel_file, file_dict) {
      // Read excel file with options to parse dates
      let rows = await readXlsxFile(excel_file, {
        dateFormat: 'DD.MM.YYYY',
        transformData: data => {
          return data.map(row => {
            return row.map(cell => {
              if (cell instanceof Date) {
                return cell.toISOString().split('T')[0];
              }
              return cell;
            });
          });
        },
      });
      let header_row = rows[0]; // The first row hold the header (dict keys)
      let data_dict = {}; // Dictionary with column names as key and values of the current row
      let header_list = []; // List of all column_names

      // Populate data_dict and header_list with column_names
      for (let i = 0; i < header_row.length; ++i) {
        let column_name = header_row[i];
        header_list.push(column_name);
        data_dict[column_name] = i; // in the future, all other rows have the [column_name] value on index i
      }

      // Populate value dict with values from all rows but the header
      let value_rows = rows.slice(1); // Remove header row
      let new_value_rows = [];
      for (let i = 0; i < value_rows.length; ++i) {
        let row = value_rows[i];
        let value_dict = {}; // Temporary dictionary to map column name and row values
        Object.assign(value_dict, data_dict);
        for (let j = 0; j < header_list.length; ++j) {
          let column_name = header_list[j]; // j: index of column_name
          value_dict[column_name] = row[data_dict[column_name]]; // and we use the column_name in our value_dict to save its value in the result
        }
        value_dict['errors'] = { keys: [] };
        if (data_dict['Analysis Profile']) {
          value_dict['analysis_profile'] = row[data_dict['Analysis Profile']];
        }
        // Include a flag for CAD-less parts
        if (!value_dict['cad']) {
          value_dict['cad_less'] = true;
        }
        new_value_rows.push(value_dict);
      }

      // Initialize the upload queue with parts from excel
      this.uploadQueue = new_value_rows.map(row => ({
        data: row,
        file_dict: file_dict,
        status: 'pending',
        part_id: null,
      }));
    },

    async processCadFiles(file_dict) {
      let cad_files = Object.keys(file_dict).filter(filename => !isExcel(getExtension(filename)));

      // Initialize the upload queue with CAD files
      this.uploadQueue = cad_files.map(cad => ({
        data: { cad, errors: { keys: [] } },
        file_dict: file_dict,
        status: 'pending',
        part_id: null,
      }));
    },

    async startUpload() {
      if (this.uploadQueue.length === 0) {
        this.$root.notify('warning', 'No files to upload', '', 3000);
        return;
      }

      if (!this.batchName.trim()) {
        this.$root.notify('warning', 'Please enter a group name', '', 3000);
        return;
      }

      try {
        this.batchUploading = true;

        // Create the batch first
        let formData = new FormData();
        formData.append('name', this.batchName);
        formData.append('size', this.uploadQueue.length);

        let [err, response] = await to(this.axiosInstance.post('/api/v1/batch/', formData));
        if (err) {
          throw err;
        }

        this.batchPk = response.data.batch_pk;
        await this.$nextTick(() => {
          this.fetchBatches();
        });

        // Start uploading files
        this.startNextUpload();
      } catch (error) {
        this.handleError(error, 'Failed to create batch.');
      }
    },

    startNextUpload() {
      // Find the next pending part in the queue
      const nextIndex = this.uploadQueue.findIndex(part => part.status === 'pending');

      if (nextIndex === -1) {
        // All parts are processed
        this.batchUploading = false;
        this.$emit('batch-upload-complete');
        return;
      }

      this.currentUploadIndex = nextIndex;
      this.uploadPart(this.uploadQueue[nextIndex]);
    },

    async uploadPart(queueItem) {
      this.uploadQueue[this.currentUploadIndex].status = 'uploading';
      const part_data = queueItem.data;
      const file_dict = queueItem.file_dict;

      let form_data_part_upload = new FormData();
      form_data_part_upload.append('batch_pk', this.batchPk);
      form_data_part_upload.append('batch_upload', true);
      form_data_part_upload.append('queue', 'batch_queue');
      form_data_part_upload.append('skip_drawing_analysis', this.checkBoxSkipDrawingAnalysis);
      form_data_part_upload.append('rip_assemblies', this.checkBoxRipAssemblies);
      form_data_part_upload.append('market_price_calculation', this.checkBoxMarketPrices);

      for (let property in part_data) {
        if (Object.prototype.hasOwnProperty.call(part_data, property) && part_data[property]) {
          form_data_part_upload.append(property, part_data[property]);
        }
      }

      if (!part_data.cad) {
        form_data_part_upload.append('is_cadless', true);
        if (part_data['material']) {
          form_data_part_upload.append('current_material', part_data['mat_name']);
        }
        if (part_data['bb_x']) {
          form_data_part_upload.append('bb_x', part_data['bb_x']);
        }
        if (part_data['bb_y']) {
          form_data_part_upload.append('bb_y', part_data['bb_y']);
        }
        if (part_data['bb_z']) {
          form_data_part_upload.append('bb_z', part_data['bb_z']);
        }
        if (part_data['volume']) {
          form_data_part_upload.append('part_vol', part_data['volume']);
        }
        if (part_data['mass']) {
          form_data_part_upload.append('current_part_mass', part_data['mass']);
        }
      } else if (isCAD(part_data['cad'])) {
        form_data_part_upload.append('file', file_dict[part_data['cad']]);
      }

      if (part_data['analysis_profile_id']) {
        form_data_part_upload.append('analysis_profile_id', part_data['analysis_profile_id']);
      }

      var err;
      var upload_response;
      const assetUpload = part_data['cad'] && !isCAD(part_data['cad']);
      if (assetUpload) {
        let formDataPartApi = this.cloneFormData(form_data_part_upload);
        const assetFileName = part_data['cad'];
        const assetFileNameCleaned = assetFileName.replace(/\..*$/, '');
        formDataPartApi.append('name', assetFileNameCleaned);

        [err, upload_response] = await to(this.axiosInstance.post('/api/v1/part/', formDataPartApi));

        let formDataAssetApi = this.cloneFormData(form_data_part_upload);
        formDataAssetApi.append('file', file_dict[part_data['cad']]);
        const partId = upload_response.data.part_id;
        [err, upload_response] = await to(this.axiosInstance.post(`/api/v1/part/${partId}/assets/`, formDataAssetApi));
      } else {
        [err, upload_response] = await to(this.axiosInstance.post('/api/v1/part/', form_data_part_upload));
      }

      if (err) {
        part_data['errors']['cad_error'] = JSON.stringify(err);
        part_data['errors']['keys'].push('cad_error');
        queueItem.status = 'error';
        this.startNextUpload();
      } else {
        part_data.part_id = upload_response.data.part_id;
        // Post image assets
        if (part_data.hasOwnProperty('images') && part_data['images']) {
          await this.uploadAssets(part_data, file_dict, 'images', 'image');
        }

        // Post drawing assets
        if (part_data.hasOwnProperty('drawings') && part_data['drawings']) {
          await this.uploadAssets(part_data, file_dict, 'drawings', 'drawing', {
            skip_drawing_analysis: this.checkBoxSkipDrawingAnalysis,
          });
        }

        // Post misc assets
        if (part_data.hasOwnProperty('misc') && part_data['misc']) {
          await this.uploadAssets(part_data, file_dict, 'misc', 'misc');
        }

        queueItem.part_id = upload_response.data.part_id;

        // Important change: For cadless parts, mark as completed immediately
        if (!part_data.cad) {
          queueItem.status = 'completed';

          // Start the next upload right away for cadless parts
          this.startNextUpload();
          return;
        }

        queueItem.status = 'uploading';
      }
    },

    cloneFormData(formDataObject) {
      const newFormData = new FormData();
      for (const [key, value] of formDataObject.entries()) {
        newFormData.append(key, value);
      }
      return newFormData;
    },

    async uploadAssets(part_data, file_dict, dataKey, assetType, additionalParams = {}) {
      let files = part_data[dataKey].split(';');
      for (let file of files) {
        let formData = new FormData();
        formData.append('file', file_dict[file]);
        formData.append('part_id', part_data.part_id);
        formData.append('asset_type', assetType);

        // Add any additional parameters
        for (let [key, value] of Object.entries(additionalParams)) {
          formData.append(key, value);
        }
        let [err, upload_response] = await to(
          this.axiosInstance.post(`api/v1/part/${part_data.part_id}/assets/`, formData)
        );
        if (err) {
          part_data['errors'][`${assetType}_error`] = JSON.stringify(err);
          part_data['errors']['keys'].push(`${assetType}_error`);
        } else {
          // A file was actually transmitted
          if (!upload_response.data.message) {
            part_data[`${assetType}_uploaded`] = true;
          }
        }
      }
    },

    handlePartUploadFinished(event) {
      const { part_name, part_id, status } = event;
      // First try to find the part by part_id (most reliable)
      let partIndex = this.uploadQueue.findIndex(part => part.part_id === part_id);

      // If not found by part_id, try to find by part_name among pending/uploading parts
      if (partIndex === -1) {
        partIndex = this.uploadQueue.findIndex(
          part => part.data.cad === part_name && (part.status === 'pending' || part.status === 'uploading')
        );
      }

      if (partIndex !== -1) {
        this.uploadQueue[partIndex].status = status === 'success' ? 'completed' : 'error';
        this.startNextUpload();
      }
    },

    handleError(err, message) {
      this.$root.notify('error', message, '', 5000);
      this.batchUploading = false;
    },

    async downloadMetadataTemplate() {
      try {
        const response = await this.axiosInstance.get('api/v1/metadata-template/', {
          responseType: 'blob',
        });

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'metadata_template.xlsx');
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading template:', error);
      }
    },
  },
};
</script>
